import { Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { useEffect, useState, type ReactNode } from 'react';
import { UNLOCK_CODE } from '@/constants';
import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import DefaultBackground from '@/components/DefaultBackground';
import Logo from '@/components/Logo';
import { config } from '@/config';

const STORAGE_KEY = 'APP_UNLOCK_CODE';

function getStoredCode(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function WebGate({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<'loading' | 'locked' | 'unlocked'>(
    'loading',
  );
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    setStatus(getStoredCode() === UNLOCK_CODE ? 'unlocked' : 'locked');
  }, []);

  if (status === 'loading') {
    return null;
  }

  if (status === 'unlocked') {
    return <>{children}</>;
  }

  const handleSubmit = () => {
    if (input === UNLOCK_CODE) {
      localStorage.setItem(STORAGE_KEY, input);
      setStatus('unlocked');
      setError(false);
    } else {
      setError(true);
    }
  };

  return (
    <ThemedView style={styles.container}>
      <DefaultBackground />

      <View style={styles.centerContent}>
        <Logo height={38} scaleProportionally />

        <View style={styles.card}>
          <ThemedText style={styles.title}>Enter Code</ThemedText>
          <ThemedText style={styles.subtitle}>
            Enter your access code to continue
          </ThemedText>

          <TextInput
            secureTextEntry
            value={input}
            onChangeText={(text) => {
              setInput(text);
              setError(false);
            }}
            onSubmitEditing={handleSubmit}
            placeholder="Code"
            placeholderTextColor="rgba(255,255,255,0.4)"
            autoFocus
            style={[styles.input, error && styles.inputError]}
          />

          {error && (
            <ThemedText style={styles.errorText}>Wrong code</ThemedText>
          )}

          <Pressable
            onPress={handleSubmit}
            style={({ pressed }) => [
              styles.button,
              pressed && styles.buttonPressed,
            ]}
          >
            <ThemedText style={styles.buttonText}>Unlock</ThemedText>
          </Pressable>
        </View>
      </View>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 32,
  },
  card: {
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderRadius: 20,
    paddingVertical: 40,
    paddingHorizontal: 48,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
  },
  title: {
    color: '#fff',
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  subtitle: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 14,
    marginBottom: 24,
  },
  input: {
    width: 260,
    paddingVertical: 14,
    paddingHorizontal: 18,
    fontSize: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.25)',
    backgroundColor: 'rgba(255,255,255,0.1)',
    color: '#fff',
    textAlign: 'center',
  },
  inputError: {
    borderWidth: 2,
    borderColor: '#e53935',
  },
  errorText: {
    color: '#ff8a8a',
    fontSize: 14,
    marginTop: 8,
  },
  button: {
    marginTop: 16,
    paddingVertical: 14,
    paddingHorizontal: 48,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  buttonPressed: {
    backgroundColor: 'rgba(255,255,255,0.35)',
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default function CodeGate({ children }: { children: ReactNode }) {
  const isWeb = Platform.OS === 'web';
  const hasUnlockCodeDefined = !!config.unlockCode;
  const shouldGateWithCode = isWeb && hasUnlockCodeDefined;

  if (shouldGateWithCode) {
    return <WebGate>{children}</WebGate>;
  }

  return <>{children}</>;
}
